import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNotification } from 'components/Notification'
import {
  updateUserUploads,
  deleteUserUploadRequest,
  getUserResourceLink,
} from 'services/userUploadRequests'
import { irwin } from 'config'
import { fetchOrCreateAuthorization } from 'services/customers'

const useRequestFormMutations = (params, uploadRequests) => {
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()

  const [deleteModalState, setDeleteModalState] = useState({
    open: false,
    requestToDelete: null,
  })
  const [createModalState, setCreateModalState] = useState({ open: false })
  const [editModalState, setEditModalState] = useState({
    open: false,
    requestToEdit: null,
  })
  const [manageModalState, setManageModalState] = useState({
    open: false,
    requestToManage: null,
  })

  // TODO: this can be removed when in-place editing is disabled
  const { mutate: updateUserUploadRequest } = useMutation(
    ({ id, ...params }) => updateUserUploads(id, params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['useUploadRequestsQuery', params], { refetchActive: true })
      },
      onError: _ =>
        createNotification({
          variant: 'error',
          message: 'Encountered an error while trying to update. Please try again later',
        }),
    },
  )

  const { mutate: deleteUploadRequestMutation, isLoading: isDeleting } = useMutation(
    deleteUserUploadRequest,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['useUploadRequestsQuery'])
        createNotification({
          message: 'Request deleted successfully',
          variant: 'success',
        })
      },
      onError: () => {
        createNotification({
          variant: 'error',
          message: 'Failed to delete request. Please try again later',
        })
      },
    },
  )

  const { mutate: loginAsAssistant, isLoading: isLoggingInAsAssistant } = useMutation(
    fetchOrCreateAuthorization,
    {
      onSuccess: (_, customerId) => {
        window.open(`${irwin.appUrl}/?customer_id=${customerId}`)
      },
      onError: (error, customerId) => {
        createNotification({
          message: `Error authorizing customer ${customerId}: ${error.message}`,
          variant: 'error',
        })
      },
    },
  )

  const handleRowChange = row => {
    const { status, assignee, internalComments } = row

    updateUserUploadRequest({
      id: row.id,
      internalComments,
      status,
      assigneeId: assignee?.id,
    })

    return row
  }

  // Create modal handlers

  const handleCreateModalOpen = () => {
    setCreateModalState({ open: true })
  }

  const handleCreateModalClose = () => {
    setCreateModalState({ open: false })
  }

  // Edit modal handlers

  const handleEditModalOpen = row => {
    // Find the original API data object instead of using the transformed table row data
    // The transformed row data from formatRowValues only contains flattened fields for display,
    // but the modal forms need the complete nested object structure from the API response
    const originalRequestData = uploadRequests.find(req => req.id === row.id)

    setEditModalState({
      open: true,
      requestToEdit: originalRequestData,
    })
  }

  const handleEditModalClose = () => {
    setEditModalState({
      open: false,
      requestToEdit: null,
    })
  }

  // Manage modal handlers

  const handleManageModalOpen = row => {
    // Find the original API data object instead of using the transformed table row data
    // The transformed row data from formatRowValues only contains flattened fields for display,
    // but the modal forms need the complete nested object structure from the API response
    const originalRequestData = uploadRequests.find(req => req.id === row.id)

    setManageModalState({
      open: true,
      requestToManage: originalRequestData,
    })
  }

  const handleManageModalClose = () => {
    setManageModalState({
      open: false,
      requestToManage: null,
    })
  }

  // Delete modal handlers

  const handleDeleteModalOpen = row => {
    setDeleteModalState({
      open: true,
      requestToDelete: {
        id: row?.id,
        requestName: row?.requestName,
      },
    })
  }

  const handleDeleteModalClose = () => {
    setDeleteModalState({
      open: false,
      requestToDelete: null,
    })
  }

  const handleDeleteConfirm = () => {
    if (deleteModalState.requestToDelete) {
      deleteUploadRequestMutation(deleteModalState.requestToDelete.id)
      handleDeleteModalClose()
    }
  }

  const handleActionLogin = row => {
    const customerId = row.customer?.id
    if (customerId) {
      loginAsAssistant(customerId)
    } else {
      createNotification({
        message: 'Unable to login: Customer information not available',
        variant: 'error',
      })
    }
  }

  // The download link generated from the BE expires after 5
  // minutes. The cached link in the FE is expired in 3 minutes
  // to give a little buffer room.
  const handleFileDownload = async (id, filePath) => {
    try {
      const { data } = await queryClient.fetchQuery(
        ['download', 'userRequest', { id, filePath }],
        () => getUserResourceLink(id, filePath),
        {
          staleTime: 1000 * 60 * 3,
        },
      )
      window.open(data.fileUrl)
    } catch (error) {
      createNotification({
        variant: 'error',
        message: 'Encountered an error while downloading the file. Please try again later',
      })
    }
  }

  return {
    // Mutation functions
    updateUserUploadRequest,
    // Row/table handlers
    handleRowChange,
    handleFileDownload,
    handleActionLogin,
    isLoggingInAsAssistant,
    // Create modal state and handlers
    createModalState,
    handleCreateModalOpen,
    handleCreateModalClose,
    // Edit modal state and handlers
    editModalState,
    handleEditModalOpen,
    handleEditModalClose,
    // Manage modal state and handlers
    manageModalState,
    handleManageModalOpen,
    handleManageModalClose,
    // Delete modal state and handlers
    deleteModalState,
    handleDeleteModalOpen,
    handleDeleteModalClose,
    handleDeleteConfirm,
    isDeleting,
  }
}

export default useRequestFormMutations
